import {
  Button,
  Dialog,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Close, Delete, Edit, RestoreFromTrash } from "@mui/icons-material";
import { useState } from "react";
import { NavLink } from "react-router-dom";
import { toast } from "react-toastify";
import { $generateHtmlFromNodes } from "@lexical/html";
import { EditorState, LexicalEditor } from "lexical";
import Editor from "../editor/Editor";
import { EmailTemplate, EmailType } from "../../common/EmailDetails";
import {
  useCreateEmailTemplateMutation,
  useDeleteEmailTemplateMutation,
  useGetEmailTemplatesQuery,
  useRestoreEmailTemplateMutation,
  useUpdateEmailTemplateMutation,
} from "../../redux/api/email";
import "./EmailTemplateAdmin.scss";

const toTemplateKey = (displayName: string) =>
  displayName.toUpperCase().trim().replace(/\s+/g, '_').replace(/[^A-Z0-9_]/g, '') || 'TEMPLATE';

const EMAIL_TYPE_OPTIONS = [
  { value: "", label: "None (automated / SES)" },
  ...Object.values(EmailType).map((v) => ({ value: v, label: v })),
];

const EmailTemplateAdmin = () => {
  const { data: templates, refetch } = useGetEmailTemplatesQuery({ includeDeleted: true });
  const [createTemplate] = useCreateEmailTemplateMutation();
  const [updateTemplate] = useUpdateEmailTemplateMutation();
  const [deleteTemplate] = useDeleteEmailTemplateMutation();
  const [restoreTemplate] = useRestoreEmailTemplateMutation();

  const [selected, setSelected] = useState<EmailTemplate | null>(null);
  const [dialogMode, setDialogMode] = useState<"create" | "edit" | "delete" | "restore" | null>(null);

  const [formKey, setFormKey] = useState(0);
  const [formDisplayName, setFormDisplayName] = useState("");
  const [formEmailType, setFormEmailType] = useState("");
  const [formSubject, setFormSubject] = useState("");
  const [formHtmlBody, setFormHtmlBody] = useState("");
  const [formAvailableVars, setFormAvailableVars] = useState("");
  const [originalHtmlBody, setOriginalHtmlBody] = useState("");

  const [varWarnDialog, setVarWarnDialog] = useState(false);
  const [pendingSave, setPendingSave] = useState<(() => void) | null>(null);

  const openCreate = () => {
    setSelected(null);
    setFormKey((k) => k + 1);
    setFormDisplayName("");
    setFormEmailType("");
    setFormSubject("");
    setFormHtmlBody("");
    setFormAvailableVars("");
    setOriginalHtmlBody("");
    setDialogMode("create");
  };

  const openEdit = (template: EmailTemplate) => {
    setSelected(template);
    setFormKey((k) => k + 1);
    setFormDisplayName(template.displayName);
    setFormEmailType(template.emailType ?? "");
    setFormSubject(template.subject);
    setFormHtmlBody(template.htmlBody);
    setFormAvailableVars(template.availableVars ?? "");
    setOriginalHtmlBody(template.htmlBody);
    setDialogMode("edit");
  };

  const handleEditorChange = (state: EditorState, editor: LexicalEditor) => {
    state.read(() => {
      setFormHtmlBody($generateHtmlFromNodes(editor, null));
    });
  };

  const checkVarsAndSave = (save: () => void) => {
    if (!formAvailableVars) {
      save();
      return;
    }
    const vars = formAvailableVars.split(",").map((v) => v.trim().split(" ")[0]);
    const missing = vars.filter((v) => v.startsWith("{{") ? !formHtmlBody.includes(v) : !formHtmlBody.includes(`{{${v}}}`));
    if (missing.length > 0) {
      setPendingSave(() => save);
      setVarWarnDialog(true);
    } else {
      save();
    }
  };

  const handleCreate = () => {
    const save = async () => {
      const response = await createTemplate({
        templateKey: toTemplateKey(formDisplayName),
        displayName: formDisplayName,
        emailType: formEmailType || null,
        subject: formSubject,
        htmlBody: formHtmlBody,
        availableVars: formAvailableVars || undefined,
      } as any);
      if (!("error" in response)) {
        refetch();
        setDialogMode(null);
        toast.success(<p>Email template created successfully.</p>);
      }
    };
    checkVarsAndSave(save);
  };

  const handleEdit = () => {
    const save = async () => {
      const response = await updateTemplate({
        id: selected!.id,
        displayName: formDisplayName,
        emailType: formEmailType || null,
        subject: formSubject,
        htmlBody: formHtmlBody,
        availableVars: formAvailableVars || undefined,
      } as any);
      if (!("error" in response)) {
        refetch();
        setDialogMode(null);
        toast.success(<p>Email template updated successfully.</p>);
      }
    };
    checkVarsAndSave(save);
  };

  const handleDelete = async () => {
    const response = await deleteTemplate(selected!.id);
    if (!("error" in response)) {
      refetch();
      setDialogMode(null);
      toast.success(<p>Email template deleted.</p>);
    }
  };

  const handleRestore = async () => {
    const response = await restoreTemplate(selected!.id);
    if (!("error" in response)) {
      refetch();
      setDialogMode(null);
      toast.success(<p>Email template restored.</p>);
    }
  };

  const missingVars = (() => {
    if (!formAvailableVars) return [];
    const vars = formAvailableVars.split(",").map((v) => v.trim().split(" ")[0]);
    return vars.filter((v) => v.startsWith("{{") ? !formHtmlBody.includes(v) : !formHtmlBody.includes(`{{${v}}}`));
  })();

  const closeEditDialog = () => setDialogMode(null);

  return (
    <>
      <div className="flex align-items-center g-3 et-admin-header">
        <h2>Email Templates</h2>
        <Button variant="contained" className="blue-button" onClick={openCreate}>
          Add New
        </Button>
      </div>
      <TableContainer className="flex wd-50">
        <Table className="table">
          <TableHead>
            <TableRow>
              <TableCell className="et-col-name">Display Name</TableCell>
              <TableCell className="et-col-type">Email Type</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {templates?.filter((r) => !r.deleted).map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.displayName}</TableCell>
                <TableCell>{row.emailType ?? "—"}</TableCell>
                <TableCell>
                  <div className="flex justify-content-center align-items-center g-3">
                    <NavLink to="" className="blue-link" onClick={() => openEdit(row)}>
                      <Edit color="primary" />
                    </NavLink>
                    <NavLink to="" className="blue-link" onClick={() => { setSelected(row); setDialogMode("delete"); }}>
                      <Delete color="primary" />
                    </NavLink>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {templates?.some((r) => r.deleted) && (
        <>
          <h3 className="et-deleted-header">Deleted Templates</h3>
          <TableContainer className="flex wd-50">
            <Table className="table">
              <TableBody>
                {templates.filter((r) => r.deleted).map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="et-col-name"><span className="et-deleted">{row.displayName}</span></TableCell>
                    <TableCell className="et-col-type"><span className="et-deleted">{row.emailType ?? "—"}</span></TableCell>
                    <TableCell>
                      <NavLink to="" className="blue-link" onClick={() => { setSelected(row); setDialogMode("restore"); }}>
                        <RestoreFromTrash color="error" />
                      </NavLink>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Create / Edit dialog */}
      <Dialog
        open={dialogMode === "create" || dialogMode === "edit"}
        onClose={closeEditDialog}
        maxWidth="lg"
        fullWidth
      >
        <div className="et-dialog-content">
          <div className="modal-header">
            <Typography variant="h6">{dialogMode === "create" ? "Create Email Template" : "Edit Email Template"}</Typography>
            <Button onClick={closeEditDialog} className="grey-link-button">
              <Close />
            </Button>
          </div>

          {formAvailableVars && (
            <div className="et-vars-banner">
              <strong>Dynamic variables (preserve in body):</strong>{" "}
              {formAvailableVars.split(",").map((v) => v.trim().split(" ")[0]).map((v) => `{{${v.replace(/[{}]/g, "")}}}`).join(", ")}
            </div>
          )}

          <TextField
            label="Display Name"
            value={formDisplayName}
            onChange={(e) => setFormDisplayName(e.target.value)}
            fullWidth
            size="small"
          />

          <div>
            <Typography variant="caption" color="text.secondary">Email Type</Typography>
            <Select
              value={formEmailType}
              onChange={(e) => setFormEmailType(e.target.value)}
              fullWidth
              size="small"
              displayEmpty
            >
              {EMAIL_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </div>

          <TextField
            label="Subject"
            value={formSubject}
            onChange={(e) => setFormSubject(e.target.value)}
            fullWidth
            size="small"
          />

          <div>
            <Typography variant="caption" color="text.secondary">Body</Typography>
            <Editor key={formKey} initialContent={originalHtmlBody} onChange={handleEditorChange} />
          </div>

          <div className="modal-footer">
            <Button variant="contained" className="blue-button" onClick={dialogMode === "create" ? handleCreate : handleEdit}>
              Save
            </Button>
            <Button className="outline-button-small" onClick={closeEditDialog}>
              Cancel
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Variable warning dialog */}
      <Dialog open={varWarnDialog} onClose={() => setVarWarnDialog(false)}>
        <div className="et-dialog-content">
          <Typography variant="h6">Missing Variables</Typography>
          <p>The following substitution variables were removed from the body:</p>
          <p><strong>{missingVars.map((v) => `{{${v.replace(/[{}]/g, "")}}}`).join(", ")}</strong></p>
          <p>Emails using this template won't include those values. Save anyway?</p>
          <div className="modal-footer">
            <Button
              className="blue-button-small"
              onClick={() => {
                setVarWarnDialog(false);
                pendingSave?.();
              }}
            >
              Save Anyway
            </Button>
            <Button className="outline-button-small" onClick={() => setVarWarnDialog(false)}>
              Cancel
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={dialogMode === "delete"} onClose={() => setDialogMode(null)}>
        <div className="et-dialog-content">
          <Typography variant="h6">Delete Template</Typography>
          <p>Delete <strong>{selected?.displayName}</strong>? It will no longer appear in email pickers.</p>
          <div className="modal-footer">
            <Button className="blue-button-small" onClick={handleDelete}>Confirm</Button>
            <Button className="outline-button-small" onClick={() => setDialogMode(null)}>Cancel</Button>
          </div>
        </div>
      </Dialog>

      {/* Restore confirm */}
      <Dialog open={dialogMode === "restore"} onClose={() => setDialogMode(null)}>
        <div className="et-dialog-content">
          <Typography variant="h6">Restore Template</Typography>
          <p>Restore <strong>{selected?.displayName}</strong>?</p>
          <div className="modal-footer">
            <Button className="blue-button-small" onClick={handleRestore}>Confirm</Button>
            <Button className="outline-button-small" onClick={() => setDialogMode(null)}>Cancel</Button>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default EmailTemplateAdmin;
